<template>
  <b-modal
    ref="modal"
    static
    :visible="show"
    :title="modalTitle"
    header-bg-variant="light"
    @hidden="resetForm"
    @close="close"
    @cancel="close"
    @ok.prevent="handleSubmit"
    :ok-disabled="isSubmitting"
    no-close-on-backdrop
    centered
  >
    <b-form @submit.prevent="handleSubmit" ref="form">
      <b-form-group
        label="Mã nhóm lỗi:"
        label-for="code-input"
        :state="isValidCode"
        :invalid-feedback="codeInvalidFeedback"
      >
        <b-form-input
          id="code-input"
          v-model="form.code"
          :state="isValidCode"
          required
          placeholder="Nhập mã nhóm lỗi"
          maxlength="50"
          :disabled="isSubmitting || (isEdit && form.technicalErrors && form.technicalErrors.length > 0)"
        ></b-form-input>
        <small class="form-text text-muted" v-if="isEdit && form.technicalErrors && form.technicalErrors.length > 0">
          Không thể thay đổi mã vì nhóm lỗi đã có lỗi kỹ thuật liên kết
        </small>
      </b-form-group>

      <b-form-group
        label="Tên nhóm lỗi:"
        label-for="name-input"
        :state="isValidName"
        :invalid-feedback="nameInvalidFeedback"
      >
        <b-form-input
          id="name-input"
          v-model="form.name"
          :state="isValidName"
          required
          placeholder="Nhập tên nhóm lỗi"
          maxlength="255"
          :disabled="isSubmitting"
        ></b-form-input>
      </b-form-group>

      <b-form-group label-for="active-input">
        <b-form-checkbox
          id="active-input"
          v-model="form.isActive"
          :disabled="isSubmitting"
          switch
        >
          {{ form.isActive ? 'Hoạt động' : 'Không hoạt động' }}
        </b-form-checkbox>
      </b-form-group>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" :disabled="isSubmitting" @click="cancel()">
        Hủy bỏ
      </b-button>
      <b-button
        variant="primary"
        :disabled="isSubmitting || !isFormValid"
        @click="handleSubmit"
      >
        <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
        {{ isEdit ? 'Cập nhật' : 'Thêm mới' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { ErrorGroupService } from './service/error-group.service';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';

export default {
  name: 'ErrorGroupFormModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    errorGroup: {
      type: Object,
      default: null,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isSubmitting: false,
      form: {
        code: '',
        name: '',
        isActive: true,
        technicalErrors: [],
      },
      validationErrors: {
        code: '',
        name: '',
      },
    };
  },
  computed: {
    modalTitle() {
      return this.isEdit
        ? `Chỉnh sửa nhóm lỗi: ${this.errorGroup?.name || ''}`
        : 'Thêm mới nhóm lỗi';
    },
    isValidCode() {
      return this.validationErrors.code ? false : null;
    },
    isValidName() {
      return this.validationErrors.name ? false : null;
    },
    codeInvalidFeedback() {
      return this.validationErrors.code;
    },
    nameInvalidFeedback() {
      return this.validationErrors.name;
    },
    isFormValid() {
      return !!this.form.code && !!this.form.name;
    },
  },
  watch: {
    errorGroup(newVal) {
      if (newVal) {
        this.initForm();
      }
    },
  },
  methods: {
    initForm() {
      if (this.errorGroup) {
        this.form = {
          ...this.form,
          ...this.errorGroup,
        };
      }
    },
    resetForm() {
      this.form = {
        code: '',
        name: '',
        isActive: true,
        technicalErrors: [],
      };
      this.validationErrors = {
        code: '',
        name: '',
      };
    },
    validateForm() {
      this.validationErrors = {
        code: '',
        name: '',
      };

      if (!this.form.code.trim()) {
        this.validationErrors.code = 'Mã nhóm lỗi không được để trống';
      }

      if (!this.form.name.trim()) {
        this.validationErrors.name = 'Tên nhóm lỗi không được để trống';
      }

      return !this.validationErrors.code && !this.validationErrors.name;
    },
    handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;
      const payload = {
        code: this.form.code.trim(),
        name: this.form.name.trim(),
        isActive: this.form.isActive,
      };

      const request = this.isEdit
        ? ErrorGroupService.update(this.errorGroup.id, payload)
        : ErrorGroupService.create(payload);

      request
        .then((response) => {
          if (response.data.status === 200) {
            const message = this.isEdit
              ? 'Cập nhật nhóm lỗi thành công!'
              : 'Thêm mới nhóm lỗi thành công!';

            makeToastSuccess(message);
            this.$emit('error-group-saved', response.data.data);
          } else {
            makeToastFaile(
              response.data.message || 'Có lỗi xảy ra, vui lòng thử lại sau!',
            );
          }
        })
        .catch((error) => {
          console.error('Error saving error group:', error);

          if (
            error.response &&
            error.response.data &&
            error.response.data.errors
          ) {
            const errors = error.response.data.errors;
            Object.keys(errors).forEach((key) => {
              if (key in this.validationErrors) {
                this.validationErrors[key] = errors[key];
              }
            });
          } else {
            makeToastFaile(
              error.message || 'Có lỗi xảy ra, vui lòng thử lại sau!',
            );
          }
        })
        .finally(() => {
          this.isSubmitting = false;
          if (!Object.values(this.validationErrors).some((v) => v)) {
            this.close();
          }
        });
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>